import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiGmwEventsListRequest, Event } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

type ApiGmwEventListRequestWithGmwBroId = ApiGmwEventsListRequest & { gmwBroId: string };

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getGmwEventSingleQuery = (uuid: string) => ({
  queryKey: ["gmw-event-single", uuid],
  queryFn: () => uploadApi.apiGmwEventsRead({ uuid }),
});

export const useGmwEventSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<Event>(queryClient, ["gmw-event"], uuid, getGmwEventSingleQuery(uuid));
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGmwEventListQuery = ({ gmwBroId, ...rest }: ApiGmwEventListRequestWithGmwBroId) => ({
  queryKey: ["gmw-event", gmwBroId, simplifyQueryParam(rest)],
  queryFn: () => uploadApi.apiGmwEventsList({ gmwBroId, ...rest }),
});

export const useGmwEventListQuery = (options: ApiGmwEventListRequestWithGmwBroId) => {
  return useQuery(getGmwEventListQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGmwEventQueries = () => {
  const queryClient = useQueryClient();
  return async (gmwBroId: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["gmw-event-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGmwEventListQuery({ gmwBroId }).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};
